import { createContext, useContext, useEffect, useState } from 'react';
import apiFetch from '@wordpress/api-fetch';
import { QuoteIcons } from '../../assets/svgIcons';

const SettingsContext = createContext(null);

/**
 * Normalize SVG so it follows `currentColor`
 */
const normalizeSvgColor = (svg) => {
  if (!svg) return svg;

  return svg
    .replace(/fill="[^"]*"/gi, '')
    .replace(/style="[^"]*"/gi, '')
    .replace('<svg', '<svg fill="currentColor"');
};

const PRELOADED = window.MCTQFW_PRELOADED_SETTINGS || {};

const DEFAULT_SETTINGS = {
  bar_width: 400,
  mctqfw_quote_bg_color: '#ffffff',
  mctqfw_quote_color: '#000000',
};

const INITIAL_SETTINGS = {
  ...DEFAULT_SETTINGS,
  ...PRELOADED,
};

const PRELOADED_QUOTE = window.MCTQFW_PRELOADED_QUOTE || {
  products: [],
  count: 0,
};

// console.log('Preloaded Quote:', PRELOADED_QUOTE);

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(INITIAL_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [iconSvg, setIconSvg] = useState(null);
  const [quoteProducts, setQuoteProducts] = useState(PRELOADED_QUOTE.products);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [quoteCount, setQuoteCount] = useState(PRELOADED_QUOTE.count);

  // FETCHES QUOTE COUNT //
  useEffect(() => {
    const fetchQuoteCount = async () => {
      try {
        const data = await apiFetch({ path: `/mctqfw/v1/quote?nocache=${Date.now()}` });
        setQuoteCount(data.count || 0);
      } catch (e) {
        console.error(e);
      }
    };
  
    fetchQuoteCount();
  }, []);

  // FETCHES SVG ICON //
  useEffect(() => {
    const matched = QuoteIcons.find(
      icon => icon.value === settings.quote_img
    );
  
    setIconSvg(
      matched
        ? normalizeSvgColor(matched.icon)
        : null
    );
  }, [settings.quote_img]);

  /* FETCHES QUOTE PRODUCTS */
  const refreshQuote = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const data = await apiFetch({ path: `/mctqfw/v1/quote?nocache=${Date.now()}` });
      setQuoteProducts([...(data.products || [])]);
      setQuoteCount(data.count || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = () => refreshQuote();
    window.addEventListener('mctqfw:forceRefresh', handler);
    return () => window.removeEventListener('mctqfw:forceRefresh', handler);
  }, []);

  /* ADD TO QUOTE */
  const addToQuote = async (productId) => {
    const res = await fetch(window.MCTQFW_GLOBALS.ajax_url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        action: 'mctqfw_productidget',
        nonce: window.MCTQFW_GLOBALS.nonce,
        id: productId,
      }),
    });

    const data = await res.json();

    if (!data.success) return;

    const product = data.data.product;

    setQuoteProducts(prev => {
      if (prev.some(p => p.id === product.id)) return prev;
      return [...prev, product];
    });

    setQuoteCount(data.data.count);
    setSidebarOpen(true);
  };

  /* REMOVE FROM QUOTE */
  const removeFromQuote = async (productId) => {
    setQuoteProducts(prev =>
      prev.filter(p => Number(p.id) !== Number(productId))
    );
  
    setQuoteCount(prev => Math.max(0, prev - 1));
  
    await fetch(window.MCTQFW_GLOBALS.ajax_url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        action: 'mctqfw_product_delete',
        nonce: window.MCTQFW_GLOBALS.nonce,
        id: productId,
      }),
    });
  };

  // CHECKS IF PRODUCT IS IN QUOTE //
  const isProductInQuote = (productId) => {
    return quoteProducts.some(
      (p) => Number(p.id) === Number(productId)
    );
  };

  /* CLEAR QUOTE (after submit) */
  const clearQuote = () => {
    setQuoteProducts([]);
    setQuoteCount(0);
  };

  return (
    <SettingsContext.Provider
      value={{
        settings,
        iconSvg,
        loading,
        isProductInQuote,
        quoteProducts,
        quoteCount,
        setQuoteCount,  
        sidebarOpen,
        openSidebar: () => setSidebarOpen(true),
        closeSidebar: () => setSidebarOpen(false),
        addToQuote,
        removeFromQuote,
        clearQuote,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

// Custom hook
export const useSettings = () => {
  const context = useContext(SettingsContext);

  if (!context) {
    throw new Error('useSettings must be used inside SettingsProvider');
  }

  return context;
};